// meetingSlice.ts
//@ts-nocheck
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiError, MeetingResponse, TeamsState, createTeamsPayload, updateTeamsPayload } from './MeetingTypes';
import { createMeetingData, deleteMeeting, fetchMeetingData, fetchMeetingDetails, updateMeeting } from '../../services/meetingService';


export const fetchMeetingList = createAsyncThunk<
    MeetingResponse,
    { baseId: number; appId: number },
    { rejectValue: string }
>(
    'base/fetchMeetingList',
    async ({ baseId, appId }, { rejectWithValue }) => {
        try {
            if (baseId === undefined || appId === undefined) {
                throw new Error('Base ID or App ID is undefined');
            }
            const response = await fetchMeetingData(baseId, appId);
            return response;
        } catch (err) {
            const error = err as Error;
            return rejectWithValue(error.message || 'Failed to fetch base data');
        }
    }
);


export const createMeeting = createAsyncThunk<MeetingResponse, { payload: createTeamsPayload; userId: number },
    { rejectValue: string }
>(
    'base/createMeeting',
    async ({ payload, userId }, { rejectWithValue }) => {
        try {
            const response = await createMeetingData(payload, userId);
            return response;
        } catch (err) {            
            const error = err as ApiError;
            return rejectWithValue(error.response?.data?.message || 'Failed to invite member');
        }
    }
);


export const deleteMeetingData = createAsyncThunk<MeetingResponse, { baseId: number, id: number }, { rejectValue: string }>(
  'base/deleteMeetingData',
  async ({ baseId, id, }, { rejectWithValue }) => {
    try {
      const response = await deleteMeeting(baseId, id);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to delete base member');
    }
  }
);

export const updateMeetingData = createAsyncThunk<MeetingResponse, { baseId: number, id: number, payload: updateTeamsPayload }, { rejectValue: string }>(
  'base/updateMeetingData',
  async ({ baseId, id, payload }, { rejectWithValue }) => {
    try {
      const response = await updateMeeting(baseId, id, payload);
      return response;
    } catch (err) {
      const error = err as ApiError;      
      return rejectWithValue(error.response?.data?.message || 'Failed to update base member');
    }
  }
);

export const fetchMeetingDetailsData = createAsyncThunk<MeetingResponse, { baseId: number, id: number }, { rejectValue: string }>(
  'base/fetchMeetingDetailsData',
  async ({ baseId, id }, { rejectWithValue }) => {
    try {
      const response = await fetchMeetingDetails(baseId, id);
      return response;
    } catch (err) {
      const error = err as ApiError;      
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch base member');
    }
  }
);




const initialState: TeamsState = {
    //meeting
    createMeetingStatus: 'idle',
    createMeetingError: null,
    createMeetingData: null,
    fetchMeetingStatus: 'idle',
    fetchMeetingError: null,
    fetchMeetingData: null,
    deleteMeetingStatus: 'idle',
    deleteMeetingError: null,
    deleteMeetingData: null,
    updateMeetingStatus: 'idle',
    updateMeetingError: null,
    updateMeetingData: null,
    fetchMeetingDetailStatus: 'idle',
    fetchMeetingDetailError: null,
    fetchMeetingDetailData: null
};

const MeetingSlice = createSlice({
    name: 'meeting',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(createMeeting.pending, (state) => {
                state.createMeetingStatus = 'loading';
            })
            .addCase(createMeeting.fulfilled, (state, action: PayloadAction<MeetingResponse>) => {
                state.createMeetingStatus = 'succeeded';
                state.createMeetingData = action.payload;
            })
            .addCase(createMeeting.rejected, (state, action) => {
                state.createMeetingStatus = 'failed';
                state.createMeetingError = action.payload || 'Failed to create meeting';
            })
            .addCase(fetchMeetingList.pending, (state) => {
                state.fetchMeetingStatus = 'loading';
            })
            .addCase(fetchMeetingList.fulfilled, (state, action: PayloadAction<MeetingResponse>) => {
                state.fetchMeetingStatus = 'succeeded';
                state.fetchMeetingData = action.payload;
            })
            .addCase(fetchMeetingList.rejected, (state, action) => {
                state.fetchMeetingStatus = 'failed';
                state.fetchMeetingError = action.payload || 'Failed to fetch meeting';
            })
            .addCase(deleteMeetingData.pending, (state) => {
                state.deleteMeetingStatus = 'loading';
            })
            .addCase(deleteMeetingData.fulfilled, (state, action: PayloadAction<MeetingResponse>) => {
                state.deleteMeetingStatus = 'succeeded';
                state.deleteMeetingData = action.payload;
            })
            .addCase(deleteMeetingData.rejected, (state, action) => {
                state.deleteMeetingStatus = 'failed';
                state.deleteMeetingError = action.payload || 'Failed to delete meeting';
            })
            .addCase(updateMeetingData.pending, (state) => {
                state.updateMeetingStatus = 'loading';
            })
            .addCase(updateMeetingData.fulfilled, (state, action: PayloadAction<MeetingResponse>) => {
                state.updateMeetingStatus = 'succeeded';
                state.updateMeetingData = action.payload;
            })
            .addCase(updateMeetingData.rejected, (state, action) => {
                state.updateMeetingStatus = 'failed';
                state.updateMeetingError = action.payload || 'Failed to update meeting';
            })
            .addCase(fetchMeetingDetailsData.pending, (state) => {
                state.fetchMeetingDetailStatus = 'loading';
            })
            .addCase(fetchMeetingDetailsData.fulfilled, (state, action: PayloadAction<MeetingResponse>) => {
                state.fetchMeetingDetailStatus = 'succeeded';
                state.fetchMeetingDetailData = action.payload;
            })
            .addCase(fetchMeetingDetailsData.rejected, (state, action) => {
                state.fetchMeetingDetailStatus = 'failed';
                state.fetchMeetingDetailError = action.payload || 'Failed to fetch meeting';
            });


    }
});

export default MeetingSlice.reducer;

